import { type HelpArticle, getArticle } from "@/help/registry";
import { getHelpSlugForRoute } from "@payrollmart-invoice/shared";
import { useRouterState } from "@tanstack/react-router";

/**
 * Resolution states for the contextual help article belonging to the current
 * route.
 *
 * - `loading` — reserved for the future Phase 7B DB-backed lookup. Synchronous
 *   resolution in Phase 7A short-circuits past this branch, but the component
 *   tests still cover the skeleton render so the branch stays alive in the UI.
 * - `empty` — no slug registered for the current pathname (or the slug
 *   resolves but no article is bundled). The dialog renders the
 *   "Browse all topics" empty state.
 * - `error` — reserved for the Markdown render error boundary; the hook itself
 *   does not produce this state in Phase 7A but the type stays in the union so
 *   the dialog's branching is exhaustive.
 * - `loaded` — article body resolved.
 */
export type HelpArticleState =
	| { state: "loading" }
	| { state: "empty"; slug: string | null }
	| { state: "error"; error: Error }
	| { state: "loaded"; article: HelpArticle };

/**
 * Resolve the help article for the current route.
 *
 * Reads `useRouterState().location.pathname`, maps it through
 * `getHelpSlugForRoute`, then looks up the bundled article via `getArticle`.
 * Returns one of the four `HelpArticleState` shapes so the caller can render a
 * 4-state component without further branching.
 */
export function useHelpArticle(): HelpArticleState {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const slug = getHelpSlugForRoute(pathname);
	if (slug == null) return { state: "empty", slug: null };
	const article = getArticle(slug);
	if (article == null) return { state: "empty", slug };
	return { state: "loaded", article };
}
